<script setup lang="ts">
import { toRef } from 'vue';
import {
  CdrText,
  CdrButton,
  IconStorefront,
  IconChat,
  IconMail,
  IconTelephone,
} from '@rei/cedar';
import type { ContactUsSectionModel } from '../../api/interfaces/contactUsSectionModel';
import type { PhoneNumbers } from './composables/usePhoneInteraction';
import { usePhoneInteraction } from './composables/usePhoneInteraction';

interface ContactUsCardProps {
  section: ContactUsSectionModel;
  helpCenterNumbers: PhoneNumbers;
  isFirst?: boolean;
}

const props = withDefaults(defineProps<ContactUsCardProps>(), {
  isFirst: false,
});

const emit = defineEmits<{
  sectionClick: [section: ContactUsSectionModel, phoneNumber?: { tel: string, callPhone }];
}>();

const helpCenterNumbersRef = toRef(() => props.helpCenterNumbers);
const { callPhone } = usePhoneInteraction(helpCenterNumbersRef);

// icons
const iconComponents = {
  IconStorefront,
  IconChat,
  IconMail,
  IconTelephone,
};

// get icon component by name
const getIconComponent = (iconName: string) => iconComponents[iconName as keyof typeof iconComponents] || IconMail;

// handle section click
const handleSectionClick = (phoneNumber?: { tel: string, callPhone }) => {
  emit('sectionClick', props.section, phoneNumber);
};
</script>

<template>
  <div class="contact-us-card">
    <div
      v-if="!!section.recommended"
      class="contact-us-recommended"
    >
      <CdrText>
        <span class="contact-us-recommended-text">
          Recommended
        </span>
      </CdrText>
    </div>
    <div
      class="contact-us-sectionValues"
      :class="isFirst ? 'contact-us-sectionValues-first' : ''"
    >
      <!-- layout for Call Us section -->
      <template v-if="section.section === 'Call Us'">
        <div class="contact-us-secVal contact-us-call-us-layout">
          <div class="contact-us-secVal-it">
            <component
              :is="getIconComponent(section.icon)"
              class="contact-us-sec-icon"
            />
            <CdrText>
              <span class="contact-us-sec-heading">
                {{ section.section }}
              </span>
            </CdrText>
          </div>
          <CdrText>
            <span class="contact-us-sec-body contact-us-call-us-body">
              {{ section.subText }}
            </span>
          </CdrText>
          <div class="contact-us-phone-buttons">
            <CdrButton
              v-if="helpCenterNumbers.usCallers"
              modifier="secondary"
              size="small"
              @click.prevent="() => handleSectionClick({ tel: helpCenterNumbers.usCallers, callPhone })"
            >
              U.S. Caller
            </CdrButton>
            <CdrButton
              v-if="helpCenterNumbers.internationalCallers"
              modifier="secondary"
              size="small"
              @click.prevent="() => handleSectionClick({ tel: helpCenterNumbers.internationalCallers, callPhone })"
            >
              International Caller
            </CdrButton>
          </div>
        </div>
      </template>

      <!-- layout for all other sections -->
      <template v-else>
        <div class="contact-us-secVal">
          <div class="contact-us-secVal-it">
            <component
              :is="getIconComponent(section.icon)"
              class="contact-us-sec-icon"
            />
            <CdrText>
              <span class="contact-us-sec-heading">
                {{ section.section }}
              </span>
            </CdrText>
          </div>
          <CdrText>
            <span class="contact-us-sec-side-text">
              {{ section.sideText }}
            </span>
          </CdrText>
        </div>
        <CdrText>
          <span class="contact-us-sec-body">
            {{ section.subText }}
          </span>
        </CdrText>
        <div class="contact-us-sec-button">
          <CdrButton
            modifier="secondary"
            @click="handleSectionClick"
          >
            {{ section.buttonContent }}
          </CdrButton>
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss">
  @import 'src/main/style/contactUs.scss';
</style>
